export const en = {
  // Common
  common: {
    loading: 'Loading...',
    error: 'Error',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    back: 'Back',
    close: 'Close',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    submit: 'Submit',
    reset: 'Reset',
    clear: 'Clear',
    view: 'View',
    details: 'Details',
    settings: 'Settings',
    logout: 'Logout',
    login: 'Login',
    signup: 'Sign Up',
    profile: 'Profile',
    collection: 'Collection',
    insights: 'Insights',
    overview: 'Overview',
    categories: 'Categories',
    preorders: 'Preorders',
    recent: 'Recent',
    total: 'Total',
    average: 'Average',
    price: 'Price',
    quantity: 'Quantity',
    brand: 'Brand',
    series: 'Series',
    type: 'Type',
    scale: 'Scale',
    source: 'Source',
    date: 'Date',
    status: 'Status',
    value: 'Value',
    items: 'Items',
    owned: 'Owned',
    preorder: 'Preorder',
    created: 'Created',
    updated: 'Updated',
    name: 'Name',
    code: 'Code',
    description: 'Description',
    image: 'Image',
    currency: 'Currency',
    language: 'Language',
    preferences: 'Preferences',
    clone: 'Clone',
    cloneSuccess: 'Toy cloned successfully',
    cloneError: 'Failed to clone toy',
    backToCollection: 'Back to Collection'
  },

  // Navigation
  nav: {
    home: 'Home',
    collection: 'Collection',
    insights: 'Insights',
    wishlist: 'Wishlist',
    settings: 'Settings',
    profile: 'Profile',
    logout: 'Logout',
    language: 'Language',
    export: 'Export',
    import: 'Import'
  },

  // Landing Page
  landing: {
    title: 'ToyPort',
    subtitle: 'The Ultimate Toy Collection Manager',
    description: 'Organize, track, and manage your toy and model collection with ease. Built by collectors, for collectors.',
    getStarted: 'Get Started',
    signUp: 'Sign Up',
    goToCollection: 'Go to Collection',
    sampleCollection: 'Sample Collection',
    features: {
      title: 'Powerful Features',
      subtitle: 'Everything you need to manage your collection',
      completeManagement: {
        title: 'Complete Management',
        description: 'Add, edit, and organize your toys with detailed information and categories.'
      },
      smartSearch: {
        title: 'Smart Search',
        description: 'Find your toys quickly with powerful search and filtering capabilities.'
      },
      multiCurrency: {
        title: 'Multi-Currency',
        description: 'Track prices in your local currency with automatic conversion support.'
      },
      preorderTracking: {
        title: 'Preorder Tracking',
        description: 'Keep track of preorders and their expected release dates.'
      },
      cloudSync: {
        title: 'Cloud Sync',
        description: 'Your collection is automatically synced across all your devices.'
      },
      secure: {
        title: 'Secure & Private',
        description: 'Your data is encrypted and private. Only you can access your collection.'
      }
    },
    howItWorks: {
      title: 'How It Works',
      subtitle: 'Get started in minutes with our simple 4-step process',
      signUp: {
        title: 'Sign Up',
        description: 'Create your free account in seconds with email or social login.'
      },
      addToys: {
        title: 'Add Your Toys',
        description: 'Add your existing collection with our easy-to-use interface.'
      },
      organize: {
        title: 'Organize',
        description: 'Categorize and tag your toys for easy management.'
      },
      monitor: {
        title: 'Monitor & Track',
        description: 'Track your collection value and get insights about your toys.'
      }
    },
    benefits: {
      title: 'Why Choose ToyPort?',
      subtitle: 'Join thousands of collectors who trust ToyPort to manage their collections',
      neverLose: 'Never lose track of your collection again',
      trackValue: 'Track the total value of your collection',
      planPurchases: 'Plan future purchases with price tracking',
      shareCollection: 'Share your collection with friends and family',
      mobileFriendly: 'Access your collection from any device',
      freeToUse: 'Completely free to use with no hidden costs'
    },
    collectorTypes: {
      title: 'Perfect for All Collectors',
      description: 'Whether you collect Gunpla, scale models, action figures, or any other toys, ToyPort has you covered.',
      startCollection: 'Start Your Collection'
    },
    cta: {
      title: 'Ready to Organize Your Collection?',
      subtitle: 'Join thousands of collectors who have already organized their collections with ToyPort.',
      createAccount: 'Create Free Account'
    }
  },

  // Collection
  collection: {
    title: 'My Toy Collection',
    addNewToy: 'Add New Toy',
    searchPlaceholder: 'Search by name, code, brand, or series...',
    noToys: 'No toys in your collection yet.',
    noSearchResults: 'No toys found matching your search.',
    addFirstToy: 'Add Your First Toy',
    showingResults: 'Showing {count} of {total} toys',
    endOfResults: 'You\'ve reached the end of your collection',
    summary: {
      title: 'Track and organize your precious toy collection',
      totalToys: 'Total Toys',
      foundToys: 'Found Toys',
      totalValue: 'Total Value',
      averagePrice: 'Average Price',
      totalQuantity: 'Total Quantity'
    },
    share: {
      description: 'Share a view-only link of your collection with friends or buyers.',
      copyLink: 'Copy public link',
      preview: 'Preview public page',
      copied: 'Public link copied to your clipboard.',
      copyFailed: 'Clipboard access is blocked. Copy this link manually: {url}',
      publishError: 'We could not refresh your public collection. Try again in a moment.',
      nothingToShare: 'Add at least one toy before sharing your collection.',
      publishing: 'Updating link…'
    },
    export: {
      title: 'Export Your Collection',
      subtitle: 'Download a backup of your toys for reports, inventory, or migration.',
      authRequired: 'You need to be signed in to export your collection.',
      loadError: 'We could not load your collection for export. Please try again.',
      empty: 'No toys to export yet. Add items to your collection first.',
      actionsTitle: 'Download files',
      actionsSubtitle: '{count, plural, one {1 item ready} other {{count} items ready}}',
      downloadCsv: 'Download CSV',
      downloadJson: 'Download JSON',
      fileHint: 'CSV works best in spreadsheets. JSON keeps all fields for data migration.',
      previewTitle: 'Preview (first {count} items)',
      previewSubtitle: '{total} total items · Prices in {currency}',
      previewFooter: '{remaining} more items not shown in preview.',
      headers: {
        name: 'Name',
        brand: 'Brand',
        series: 'Series',
        type: 'Type',
        scale: 'Scale',
        code: 'Code',
        quantity: 'Quantity',
        price: 'Price',
        priceCurrency: 'Price Currency',
        preorderPrice: 'Preorder Price',
        preorderPriceCurrency: 'Preorder Currency',
        source: 'Source',
        note: 'Notes',
        createdDate: 'Created Date'
      }
    },
    import: {
      title: 'Import from MyFigureCollection',
      subtitle: 'Plan a migration by referencing any public MyFigureCollection profile.',
      disclaimer: 'This is an early preview. We only show a sample parsing of the public profile right now—no data is imported into ToyPort yet.',
      form: {
        title: 'Profile URL',
        subtitle: 'Paste the link to your public profile to generate a crawl plan.',
        profileUrl: 'MyFigureCollection profile link',
        helper: 'Example: https://myfigurecollection.net/profile/makuroko'
      },
      actions: {
        preview: 'Preview data',
        loadingPreview: 'Loading preview…',
        plan: 'Generate import plan',
        loadingPlan: 'Planning…',
        planDisabled: 'Import plan coming soon'
      },
      preview: {
        title: '{username}\'s public summary',
        lastSynced: 'Last checked: {timestamp}',
        owned: '{count} owned',
        ordered: '{count} ordered',
        wished: '{count} wished',
        listed: '{count} listed for sale',
        sampleItems: 'Sample items that will be mapped',
        sampleHint: 'Full crawling will fetch every public entry. We show the first few items returned by the public RSS feeds.',
        emptyFeeds: 'We could not find items in the public feeds for this profile.',
        itemMeta: '{scale} • {code} • Released {release} • Status: {status}'
      },
      plan: {
        title: 'Import plan (coming soon)',
        subtitle: 'Connect the crawler to bring these entries into ToyPort.',
        pending: 'Automatic importing is not available yet.',
        nextSteps: 'Suggested next steps for implementation',
        disabled: 'Import planning is disabled for now. Use the preview to scope the integration and watch for updates.'
      }
    }
  },

  // Toy Detail
  toyDetail: {
    newToy: 'Add New Toy',
    editToy: 'Edit Toy',
    name: 'Name',
    code: 'Code',
    codePlaceholder: 'Optional product code',
    brand: 'Brand',
    series: 'Series',
    type: 'Type',
    scale: 'Scale',
    price: 'Price',
    preorderPrice: 'Preorder Price',
    quantity: 'Quantity',
    source: 'Source',
    imageUrl: 'Image URL',
    createdDate: 'Created Date',
    notes: 'Notes',
    save: 'Save Toy',
    cancel: 'Cancel',
    delete: 'Delete Toy',
    deleteConfirm: 'Are you sure you want to delete this toy?',
    deleteConfirmDescription: 'This action cannot be undone.',
    imagePlaceholder: 'No Image'
  },

  // Public Collection Page
  publicCollection: {
    title: 'Shared Collection',
    subtitle: 'Curated by collector {collectorId}',
    backToLanding: 'Back to ToyPort',
    viewOnlyNotice: 'This page is read-only. Prices shown in {currency}.',
    emptyTitle: 'No items to show yet',
    emptyDescription: 'The owner has not added any items or keeps them private.',
    invalidLink: 'This public collection link is invalid.',
    loadError: 'We could not load this collection right now. Please try again later.',
    notShared: 'This collector has not published a public snapshot yet.',
    untitledToy: 'Untitled Toy',
    unknownBrand: 'Unknown brand'
  },

  // Insights
  insights: {
    title: 'Collection Insights',
    subtitle: 'Detailed analytics and insights about your toy collection',
    overview: {
      title: 'Overview',
      collectionStatus: 'Collection Status',
      ownedItems: 'Owned Items',
      preorderItems: 'Preorder Items',
      valueBreakdown: 'Value Breakdown',
      ownedValue: 'Owned Value',
      preorderValue: 'Preorder Value',
      mostValuableItems: 'Most Valuable Items'
    },
    categories: {
      title: 'Categories',
      topBrands: 'Top Brands',
      topSeries: 'Top Series',
      topTypes: 'Top Types',
      topScales: 'Top Scales',
      topSources: 'Top Sources'
    },
    preorders: {
      title: 'Preorders',
      preorderSummary: 'Preorder Summary',
      items: 'Items',
      totalValue: 'Total Value'
    },
    recent: {
      title: 'Recent',
      recentAdditions: 'Recent Additions'
    },
    stats: {
      totalItems: 'Total Items',
      totalValue: 'Total Value',
      totalQuantity: 'Total Quantity',
      averagePrice: 'Average Price'
    }
  },

  // Settings
  settings: {
    title: 'User Settings',
    currency: 'Currency',
    language: 'Language',
    saveSettings: 'Save Settings',
    settingsSaved: 'Settings saved successfully!',
    settingsError: 'Failed to save settings',
    currencyDescription: 'Set your preferred currency for displaying prices throughout the application. All prices will be converted to your selected currency.',
    languageDescription: 'Choose your preferred language for the application interface. The language will be applied immediately.',
    preferences: 'Preferences'
  },

  // Custom Data
  customData: {
    addNew: 'Add New {type}',
    add: 'Add',
    adding: 'Adding...',
    valueRequired: 'Value is required',
    userNotAuthenticated: 'User not authenticated',
    addError: 'Failed to add custom data',
    description: 'This {type} will be added to your personal list and will only be visible to you.',
    enterValue: 'Enter {type} name',
    tip: 'Can\'t find your {type}? Type it and select it or tab out to add it to your personal list.',
    standard: 'Standard',
    manageTitle: 'Manage Custom Data',
    manageDescription: 'Manage your personal brand, series, type, scale, and source entries. These are only visible to you.',
    manageSubtitle: 'Personal Master Data',
    manageSubDescription: 'Add and manage your own brand, series, type, scale, and source entries that are not in the standard list.',
    manageButton: 'Manage Custom Data',
    loadError: 'Failed to load custom data',
    deleteError: 'Failed to delete custom data',
    delete: 'Delete',
    items: 'items',
    noItems: 'No custom {type} items yet.',
    customItem: 'Custom item'
  },

  // Auth
  auth: {
    login: {
      title: 'Sign In to ToyPort',
      email: 'Email',
      password: 'Password',
      signIn: 'Sign In',
      signInWithGoogle: 'Sign in with Google',
      noAccount: "Don't have an account?",
      signUp: 'Sign Up',
      forgotPassword: 'Forgot password?',
      loginError: 'Failed to log in'
    },
    signup: {
      title: 'Create Your Account',
      displayName: 'Display Name',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      signUp: 'Sign Up',
      signUpWithGoogle: 'Sign up with Google',
      haveAccount: 'Already have an account?',
      signIn: 'Sign In',
      signupError: 'Failed to create an account',
      passwordMismatch: 'Passwords do not match'
    }
  },

  // Languages
  languages: {
    en: 'English',
    th: 'ไทย',
    ja: '日本語',
    zh: '中文'
  },

  // Wishlist
  wishlist: {
    title: 'Wishlist',
    subtitle: 'Track items you want to add to your collection',
    addNewWish: 'Add Wish Item',
    addFirstWish: 'Add Your First Wish Item',
    searchPlaceholder: 'Search wishlist items...',
    noWishItems: 'No items in your wishlist yet.',
    noSearchResults: 'No wishlist items found matching your search.',
    showingResults: 'Showing {count} of {total} items',
    convertToToy: 'Convert to Toy',
    convert: 'Yes, I got them!',
    converting: 'Converting...',
    convertConfirm: 'Did you get them?',
    convertConfirmDescription: 'This will move this item from your wishlist to your collection. You can then add quantity and source information.',
    deleteConfirm: 'Delete Wish Item?',
    deleteConfirmDescription: 'This will permanently delete this wish item. This action cannot be undone.',
    // New celebratory conversion messages
    congratulations: '🎉 Congratulations! 🎉',
    finallyGotIt: 'You finally got your hands on it!',
    thatsAwesome: "That's awesome! 🎊 Time to move this beauty from your wishlist to your collection. You can add details like quantity and where you got it from.",
    readyToCelebrate: 'Ready to celebrate this addition to your collection?',
    maybeLater: 'Maybe Later',
    yesAddToCollection: '🎉 Yes, Add to Collection!',
    addingToCollection: 'Adding to Collection...'
  },

  // Currencies
  currencies: {
    USD: 'US Dollar',
    THB: 'Thai Baht',
    JPY: 'Japanese Yen',
    CNY: 'Chinese Yuan'
  }
}; 
